// MACHINERY V2 (Stroje) — fleet filter rail (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2 + §9 D4. Reference impl:
// design_handoff_machinery_v3/machinery-shared.jsx (FilterRail, RailRow,
// StatusFilterRow).
//
// SECTIONS: Stav (the 9 canonical states) · Typ stroja (+ the manufacturer
// sub-filter that appears once a type is chosen) · Lokácia.
//
// THE LOCATION SECTION IS THE §9 D4 CARRY, and it is where the live grid's
// "VISUAL SCAFFOLD — does not yet filter" (data package §9) finally filters.
// It joins the rail idiom the designer drew for the type list: same row, same
// count, same accent-soft selected state, two-level hierarchy indented.
//
// THE MANUFACTURER SUB-FILTER IS A PARITY CARRY, not an invention: the live
// grid derives it from the user's OWN machines of the selected type and sends
// it as `MachinesQuery.manufacturerId`. D4 ruled on Duplicate / decommissioned
// / group-by / location and was silent on this one, so the data package's §9
// page-state row governs and it stays. It needs no v2 slice field — it lives in
// the shared query, exactly as it does today.
//
// `railCollapsed` (machineryV2UiSlice) collapses the rail to a thin strip with
// a re-open affordance; the fleet grid takes the reclaimed width.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineType } from "../../models/models";
import StatusIcon from "./StatusIcon";
import { MachineryV2CardState } from "./cardStates";
import { FleetLocationNode } from "./fleetSelectors";
import { FLEET_STATE_FILTERS, filterInk } from "./fleetStatusFilters";
import { getMachineTypeLabel } from "../../../../shared/utils/machineUtils";

/** M2.8 exports these three atoms to `catalog/CatalogFilterRail.tsx`. The
 *  catalog rail is a DIFFERENT rail (248px, its own sections, range inputs) but
 *  it draws the same ROWS, and §9 D6's "styled per the rail idiom" is only
 *  guaranteed if there is one implementation of the row. Same cross-surface
 *  import precedent as add/AddMachinePicker.tsx reusing profile/tabs/TabDialog.
 *  The default export and every existing prop are untouched.
 *
 *  M2.16a ADDED `size` AND FORKED NOTHING. The mobile filter sheet draws the
 *  same control at 19px with a 1.5px border (the board's MbSheetRow,
 *  mobile/machinery-mobile.jsx:119) because a 13px tick is not a touch target.
 *  A second checkbox in mobile/ would be exactly the duplication the export
 *  above exists to prevent, so the ATOM grew instead. `size` DEFAULTS TO 13 and
 *  every other value derives from it, so the two rails render byte-identically
 *  to before this prop existed. THIRD consumer: mobile/sheetAtoms.tsx. */
export const CheckBox: React.FC<{ checked: boolean; size?: 13 | 19 }> = ({
  checked,
  size = 13,
}) => (
  <span
    style={{
      width: size, height: size, borderRadius: size === 19 ? 5 : 3, flexShrink: 0,
      border:
        (size === 19 ? "1.5px solid " : "1px solid ") +
        (checked ? "var(--mod-accent)" : "var(--border)"),
      background: checked ? "var(--mod-accent)" : "var(--card)",
      display: "inline-grid", placeItems: "center", color: "var(--mod-accent-fg)",
    }}
  >
    {checked && (
      <svg
        viewBox="0 0 10 10" width={size === 19 ? 11 : 8} height={size === 19 ? 11 : 8}
        aria-hidden
      >
        <path
          d="M2 5.2 L4.2 7.4 L8 3" stroke="currentColor" strokeWidth="1.8"
          fill="none" strokeLinecap="round" strokeLinejoin="round"
        />
      </svg>
    )}
  </span>
);

export interface RowProps {
  label: string;
  count?: number;
  active?: boolean;
  depth?: number;
  check?: boolean;
  ink?: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

export const Row: React.FC<RowProps> = ({
  label, count, active = false, depth = 0, check = false, ink, icon, onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      width: "100%", padding: "6px 8px", paddingLeft: 8 + depth * 16,
      borderRadius: 6, background: active ? "var(--mod-accent-soft)" : "transparent",
      border: 0, borderLeft: "2px solid " + (active ? "var(--mod-accent)" : "transparent"),
      cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
      fontFamily: "inherit", fontSize: 13, textAlign: "left",
      color: active ? "var(--mod-accent)" : "var(--ink-2)",
      fontWeight: active ? 600 : 500, minWidth: 0,
    }}
  >
    {check && <CheckBox checked={active} />}
    {icon && <span style={{ color: ink, display: "inline-flex" }}>{icon}</span>}
    <span
      style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
    >
      {label}
    </span>
    {count !== undefined && (
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--faint)" }}>
        {count}
      </span>
    )}
  </button>
);

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <div className="eyebrow" style={{ marginBottom: 6, padding: "0 4px" }}>
      {title}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>{children}</div>
  </div>
);

export interface FilterRailProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  stateCounts: Record<string, number>;
  selectedStates: MachineryV2CardState[];
  onToggleState: (state: MachineryV2CardState) => void;
  types: MachineType[];
  typeCounts: Record<string, number>;
  selectedTypeId?: string;
  onSelectType: (typeId?: string) => void;
  manufacturers: { id: string; name: string }[];
  selectedManufacturerId?: string;
  onSelectManufacturer: (id?: string) => void;
  locations: FleetLocationNode[];
  selectedLocation: string | null;
  onSelectLocation: (id: string | null) => void;
  totalCount: number;
}

const FilterRail: React.FC<FilterRailProps> = ({
  collapsed, onToggleCollapsed, stateCounts, selectedStates, onToggleState,
  types, typeCounts, selectedTypeId, onSelectType, manufacturers,
  selectedManufacturerId, onSelectManufacturer, locations, selectedLocation,
  onSelectLocation, totalCount,
}) => {
  const { translate } = useTranslation("myMachinery");

  if (collapsed) {
    return (
      <aside
        style={{
          width: 40, flexShrink: 0, background: "var(--bg-soft)",
          borderRight: "1px solid var(--border)", display: "flex",
          justifyContent: "center", paddingTop: 14,
        }}
      >
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onToggleCollapsed}
          aria-label={translate("myMachinery:v2.fleet.expandFilters")}
          style={{ width: 28, height: 28, padding: 0, justifyContent: "center" }}
        >
          ›
        </button>
      </aside>
    );
  }

  return (
    <aside
      style={{
        width: 232, flexShrink: 0, padding: "16px 14px", background: "var(--bg-soft)",
        borderRight: "1px solid var(--border)", display: "flex",
        flexDirection: "column", gap: 16, overflowY: "auto", minHeight: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="eyebrow" style={{ flex: 1 }}>
          {translate("myMachinery:v2.fleet.filters")}
        </span>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onToggleCollapsed}
          aria-label={translate("myMachinery:v2.fleet.collapseFilters")}
          style={{ width: 24, height: 24, padding: 0, justifyContent: "center" }}
        >
          ‹
        </button>
      </div>

      <Section title={translate("myMachinery:v2.fleet.status")}>
        {FLEET_STATE_FILTERS.map((s) => (
          <Row
            key={s.state}
            label={translate(s.labelKey)}
            count={stateCounts[s.state] || 0}
            active={selectedStates.indexOf(s.state) >= 0}
            check
            ink={filterInk(s.token)}
            icon={s.icon ? <StatusIcon kind={s.icon} size={11} /> : undefined}
            onClick={() => onToggleState(s.state)}
          />
        ))}
      </Section>

      <Section title={translate("myMachinery:machineType")}>
        <Row
          label={translate("myMachinery:v2.fleet.allTypes")}
          count={totalCount}
          active={!selectedTypeId}
          onClick={() => onSelectType(undefined)}
        />
        {types.map((t) => (
          <React.Fragment key={t.id}>
            <Row
              label={getMachineTypeLabel(t, translate)}
              count={typeCounts[t.name] || 0}
              active={selectedTypeId === t.id}
              onClick={() => onSelectType(selectedTypeId === t.id ? undefined : t.id)}
            />
            {selectedTypeId === t.id &&
              manufacturers.map((m) => (
                <Row
                  key={m.id}
                  label={m.name}
                  depth={1}
                  active={selectedManufacturerId === m.id}
                  onClick={() =>
                    onSelectManufacturer(selectedManufacturerId === m.id ? undefined : m.id)
                  }
                />
              ))}
          </React.Fragment>
        ))}
      </Section>

      {locations.length > 0 && (
        <Section title={translate("myMachinery:locationFilter")}>
          {locations.map((node) => (
            <React.Fragment key={node.id}>
              <Row
                label={node.name}
                count={node.count}
                active={selectedLocation === node.id}
                check
                onClick={() =>
                  onSelectLocation(selectedLocation === node.id ? null : node.id)
                }
              />
              {node.children.map((child) => (
                <Row
                  key={child.id}
                  label={child.name}
                  count={child.count}
                  depth={1}
                  active={selectedLocation === child.id}
                  check
                  onClick={() =>
                    onSelectLocation(selectedLocation === child.id ? null : child.id)
                  }
                />
              ))}
            </React.Fragment>
          ))}
        </Section>
      )}
    </aside>
  );
};

export default FilterRail;
